import { Shield, Bell, Check, Zap, Server, ArrowRight, Terminal, Eye, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

interface LandingProps {
  onLogin: () => void;
  onRegister: () => void;
}

export default function Landing({ onLogin, onRegister }: LandingProps) {
  const features = [
    {
      icon: Server,
      title: 'Multi-Server Monitoring',
      description: 'Track disk usage across all your Linux servers from a single dashboard. No more SSH-ing into each server.',
    },
    {
      icon: Eye,
      title: 'Real-time Visibility',
      description: 'See what\'s consuming your disk space with detailed breakdowns of top files and folders.',
    },
    {
      icon: Bell,
      title: 'Smart Alerts',
      description: 'Get notified when disk usage changes significantly. Choose hourly, 6-hourly, or daily alerts.',
    },
    {
      icon: Zap,
      title: 'AI-Powered Insights',
      description: 'Understand why your disk space is growing with intelligent analysis and recommendations.',
    },
    {
      icon: Terminal,
      title: 'Easy Installation',
      description: 'Install our lightweight client with a single command. Works on any Linux distribution.',
    },
    {
      icon: Shield,
      title: 'Secure by Design',
      description: 'Your data is encrypted and secure. We only collect what\'s necessary for monitoring.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      description: 'Perfect for personal projects',
      servers: 1,
      features: [
        '1 server monitoring',
        'Basic disk usage alerts',
        '7-day history retention',
        'Email notifications',
        'Community support',
      ],
      cta: 'Get Started Free',
      popular: false,
    },
    {
      name: 'Starter',
      price: 5.99,
      period: '/month',
      description: 'For growing teams and projects',
      servers: 10,
      features: [
        '10 server monitoring',
        'Smart alerts with thresholds',
        '30-day history retention',
        'Email + webhook notifications',
        'Priority email support',
        'API access',
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Professional',
      price: 10.99,
      period: '/month',
      description: 'For production environments',
      servers: 20,
      features: [
        '20 server monitoring',
        'Priority alerts',
        '90-day history retention',
        'All notification channels',
        'AI-powered insights',
        'Priority support',
        'Custom integrations',
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
  ];

  const steps = [
    {
      step: 1,
      title: 'Install the Client',
      description: 'Run a single command on your Linux server to install our lightweight monitoring agent.',
      code: 'curl -fsSL https://watchthesize.com/install.sh | bash',
    },
    {
      step: 2,
      title: 'Link Your Device',
      description: 'Click the link shown after installation to connect your server to your account.',
    },
    {
      step: 3,
      title: 'Monitor & Get Alerts',
      description: 'View your dashboard and receive alerts when disk usage changes significantly.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg shadow-lg shadow-violet-500/25">
              📊
            </div>
            <span className="text-xl font-bold text-white">WatchTheSize</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-sm text-zinc-400 hover:text-white transition-colors">How it Works</a>
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors">Pricing</a>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={onLogin}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={onRegister}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.02]"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-900/20 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-violet-500/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-fuchsia-500/10 rounded-full blur-[100px]" />
        
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>Now monitoring 10,000+ servers worldwide</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight">
            Never Run Out of
            <br />
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
              Disk Space
            </span>
            {' '}Again
          </h1>
          
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Monitor disk usage across all your Linux servers. Get real-time alerts, see what's consuming space, and receive AI-powered insights to prevent outages.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onRegister}
              className="group flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-base font-semibold text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.02]"
            >
              Start Free Monitoring
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="#how-it-works"
              className="flex items-center gap-2 px-8 py-4 rounded-xl bg-white/5 border border-white/10 text-base font-semibold text-white hover:bg-white/10 transition-all"
            >
              See How It Works
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-20">
            {[
              { value: '10K+', label: 'Servers Monitored' },
              { value: '99.9%', label: 'Uptime' },
              { value: '< 1min', label: 'Alert Response' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-zinc-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-zinc-600" />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need to
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                Monitor Disk Usage
              </span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Stop checking disk space manually. Let WatchTheSize do the heavy lifting while you focus on building great products.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div
                  key={i}
                  className="group p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] hover:border-white/10 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-violet-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get Started in
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent"> Minutes</span>
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              No complex configuration required. Install, link, and start monitoring.
            </p>
          </div>

          <div className="space-y-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className="relative flex gap-6 p-6 rounded-2xl bg-white/[0.02] border border-white/5"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-lg font-bold text-white">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-zinc-400 mb-3">{step.description}</p>
                  {step.code && (
                    <div className="p-4 rounded-xl bg-black/50 font-mono text-sm text-emerald-400 overflow-x-auto">
                      <span className="text-zinc-500">$ </span>
                      {step.code}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Simple,
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent"> Transparent </span>
              Pricing
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Start free, upgrade when you need more. No hidden fees, no surprises.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                className={`relative p-8 rounded-2xl border ${
                  plan.popular
                    ? 'bg-gradient-to-b from-violet-500/10 to-fuchsia-500/10 border-violet-500/30'
                    : 'bg-white/[0.02] border-white/5'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-xs font-semibold text-white">
                    Most Popular
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-zinc-400">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">
                      {plan.price === 0 ? 'Free' : `$${plan.price}`}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-zinc-400">{plan.period}</span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-violet-400 font-medium">
                    {plan.servers} server{plan.servers > 1 ? 's' : ''} included
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onRegister}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:scale-[1.02]'
                      : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-zinc-500 mt-8">
            All plans include 14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-br from-violet-500/10 via-fuchsia-500/10 to-pink-500/10 border border-white/10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Take Control of Your Disk Space?
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">
              Join thousands of developers who trust WatchTheSize to keep their servers running smoothly.
            </p>
            <button
              onClick={onRegister}
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-base font-semibold text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all hover:scale-[1.02]"
            >
              Start Monitoring for Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm">
                📊
              </div>
              <span className="text-sm font-semibold text-white">WatchTheSize</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
            
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} WatchTheSize. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}


